import { useEffect, useState } from "react";
import { Cube, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Equipment item switcher — a tender can carry several equipment types; each item owns its
 * pack engine, specs, design, validation, BOM, drawings and 3D model. Activating an item
 * swaps the working item on the server, so every module follows the selection.
 */
export const EquipmentSwitcher = ({ projectId, onSwitched, compact = false }) => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!projectId) return setItems([]);
    try {
      const { data } = await api.get(`/projects/${projectId}/equipment`);
      setItems(data.items || []);
      setActive(data.active_equipment_id || "");
    } catch { /* project may not be readable yet */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const activate = async (iid) => {
    if (!iid || iid === active || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/equipment/${iid}/activate`);
      setItems(data.items || []);
      setActive(data.active_equipment_id);
      const it = (data.items || []).find((i) => i.id === data.active_equipment_id);
      toast.success(`Switched to ${it?.tag || "item"} — ${it?.name || ""}`);
      onSwitched?.(data.active_equipment_id, it);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch equipment item");
    } finally { setBusy(false); }
  };

  if (!projectId || items.length < 1) return null;

  // variants of the same equipment type form one family — switch with a single click
  const families = [];
  items.forEach((i) => {
    const key = i.equipment_type_name || i.equipment_type || i.industry || "Equipment";
    const f = families.find((x) => x.key === key);
    (f || families[families.push({ key, items: [] }) - 1]).items.push(i);
  });
  const activeItem = items.find((i) => i.id === active);
  const activeFamily = families.find((f) => f.items.some((i) => i.id === active)) || families[0];
  const variantLabel = (i) => {
    const bits = (i.name || "").split(/[,·]/).map((x) => x.trim()).filter(Boolean);
    return (bits[0] || i.tag) + (i.quantity > 1 ? ` ×${i.quantity}` : "");
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid="equipment-family-switcher">
      {families.length > 0 && activeFamily && activeFamily.items.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-[var(--c-muted)] flex items-center gap-1">
            <Cube size={12} /> {activeFamily.key}
            <span className="font-mono">({activeFamily.items.length} variants)</span>
          </span>
          {activeFamily.items.map((i) => (
            <button key={i.id} onClick={() => activate(i.id)} disabled={busy}
              data-testid={`equipment-variant-${i.tag}`}
              title={i.name}
              className={`px-2 py-1 rounded-sm text-[11px] font-mono border transition-colors duration-150 ${
                i.id === active
                  ? "bg-[var(--c-accent)] border-[var(--c-accent)] text-white"
                  : "bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:border-[var(--c-accent)]"
              }`}>
              {variantLabel(i)}
              {i.has_design && <span className="ml-1 text-[9px] opacity-70">✓</span>}
            </button>
          ))}
        </div>
      )}
    <div className="flex items-center gap-2" data-testid="equipment-switcher">
      {!compact && (
        <span className="text-[10px] uppercase tracking-wide text-[var(--c-muted)] flex items-center gap-1">
          <Cube size={12} /> Equipment
        </span>
      )}
      <Select value={active} onValueChange={activate}>
        <SelectTrigger data-testid="equipment-item-select"
          className="w-72 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
          <SelectValue placeholder="Equipment item…" />
        </SelectTrigger>
        <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
          {items.map((i) => (
            <SelectItem key={i.id} value={i.id} data-testid={`equipment-item-${i.tag}`}
              className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
              <span className="font-mono text-[var(--c-muted)] mr-2">{i.tag}</span>
              {i.name}
              {i.quantity > 1 ? ` x${i.quantity}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-[10px] text-[var(--c-muted)] font-mono" data-testid="equipment-count">
        {items.length} item{items.length > 1 ? "s" : ""}
        {families.length > 1 ? ` · ${families.length} families` : ""}
      </span>
    </div>
    </div>
  );
};

export const EquipmentRollup = ({ projectId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/equipment/rollup`).then(({ data }) => setData(data)).catch(() => {});
  }, [projectId]);
  if (!data || (data.items || []).length < 2) return null;
  return (
    <div className="border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="equipment-rollup">
      <div className="px-3 py-2 bg-[var(--c-surface-2)] text-xs font-medium text-[var(--c-text)] flex items-center gap-2">
        <Plus size={12} /> Equipment schedule — {data.totals.equipment_items} types, {data.totals.units} units
      </div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-[var(--c-muted)]">
          <tr><th className="text-left px-3 py-1.5">Tag</th><th className="text-left">Equipment</th>
            <th className="text-right">Qty</th><th className="text-right">Design</th>
            <th className="text-right">Drawings</th><th className="text-right px-3">3D</th></tr>
        </thead>
        <tbody>
          {data.items.map((r) => (
            <tr key={r.id} className="border-t border-[var(--c-border)]" data-testid={`rollup-row-${r.tag}`}>
              <td className="px-3 py-1.5 font-mono text-[var(--c-muted)]">{r.tag}</td>
              <td className="text-[var(--c-text)]">{r.name}</td>
              <td className="text-right">{r.quantity}</td>
              <td className="text-right">{r.has_design ? "✓" : "—"}</td>
              <td className="text-right">{r.drawings}</td>
              <td className="text-right px-3">{r.has_3d ? "✓" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
